import React, { forwardRef, useEffect, useState } from "react";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { getAPIClient } from "../../apiClient";
import { Squirrel } from "../../components/spinner/Squirrel";
import { useTranslation } from "react-i18next";
import { ErrorModal } from "../../components/modals/ErrorModal";
import {
  loadableCompleteVerifiedRecordsAtom,
  izProviderConfigsAtom,
  userAtom,
  isHydrated,
} from "../../jotai/atoms";
import {
  ModalV2,
  ModalV2BackButton,
  ModalV2Body,
  ModalV2Header,
  ModalV2Props,
} from "../../components/modals/ModalV2";
import QRCode from "react-qr-code";
import { attempt } from "lodash";

interface SmartHealthCardModalProps {
  recordId: string | null;
  onClose: ModalV2Props["onClose"];
}

export const SmartHealthCardModal = forwardRef<HTMLDialogElement, SmartHealthCardModalProps>(
  function SmartHealthCardModal({ recordId, onClose }, ref) {
    const { t } = useTranslation();
    const [user] = useAtom(userAtom);
    const [allVerifiedRecords] = useAtom(loadableCompleteVerifiedRecordsAtom);
    const [providerConfigs] = useAtom(izProviderConfigsAtom);
    const [qrCode, setQrCode] = useState("");
    const [loadingVCI, setLoadingVCI] = useState(false);
    const [attemptMade, setAttemptMade] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState<string>();
    const isIOSMobile = /iphone|ipad/i.test(navigator.userAgent);

    const currRecord = React.useMemo(() => {
      if (
        !recordId ||
        allVerifiedRecords.state !== "hasData" ||
        !isHydrated(allVerifiedRecords.data)
      ) {
        return null;
      }
      return allVerifiedRecords.data.find((record) => record.record.uid === recordId);
    }, [allVerifiedRecords, recordId]);

    const providerConfig = isHydrated(providerConfigs)
      ? providerConfigs.find(
          (config) =>
            config.iz_provider.destination_id.toLowerCase() ===
            currRecord?.record?.izProviderKey.toLowerCase()
        )
      : undefined;

    // Reset state when recordId changes (modal opens with new record)
    useEffect(() => {
      if (recordId) {
        setQrCode("");
        setLoadingVCI(false);
        setShowErrorModal(undefined);
      }
    }, [recordId]);

    useEffect(() => {
      if (currRecord?.record && qrCode.length === 0 && !loadingVCI && !attemptMade && recordId) {
        setLoadingVCI(true);
        setAttemptMade(true);
        const fetchQRCode = async () => {
          return await getAPIClient().getVCICode(
            currRecord.record.uid,
            currRecord.record.izProviderKey
          );
        };
        fetchQRCode()
          .then((code) => {
            setQrCode(code);
            setLoadingVCI(false);
          })
          .catch((error) => {
            setLoadingVCI(false);
            setShowErrorModal("Unable to fetch QR code. Please try again later.");
          });
      }
    }, [qrCode, currRecord?.record, recordId, attemptMade, loadingVCI]);

    const renderContent = () => {
      if (allVerifiedRecords.state === "loading") {
        return <Squirrel />;
      }

      if (!recordId || !currRecord?.record.uid || !providerConfig?.features.qr_code) {
        return (
          <div className="p-4 text-center">
            {/* TODO */}
            <p>Not Found</p>
          </div>
        );
      }

      return (
        <div className="p-2 bg-qr-card-blue rounded-md flex flex-col gap-[8px]">
          {showErrorModal && (
            <ErrorModal
              title={t("generic.error")}
              onCloseModal={() => setShowErrorModal(undefined)}
            >
              <p>{showErrorModal}</p>
            </ErrorModal>
          )}
          <div className="flex flex-col">
            <div className="flex  items-center gap-2">
              <p className="font-bold text-lg">COVID-19 Vaccination</p>
            </div>
            <div className="flex gap-2">
              <p className="text-lg font-bold">Name:</p>
              <p className="my-auto">{`${currRecord?.record.lastName}, ${currRecord?.record.firstName}`}</p>
            </div>
            <div className="flex gap-2">
              <p className="text-lg font-bold">DOB:</p>
              <p className="my-auto">{`${
                currRecord?.search.childDateOfBirth || currRecord?.search.dateOfBirth
                  ? DateTime.fromISO(
                      currRecord?.search.childDateOfBirth || currRecord?.search.dateOfBirth
                    ).toLocaleString()
                  : ""
              }`}</p>
            </div>
          </div>
          <div className="flex flex-col gap-4 mx-6">
            <div className="flex items-center justify-center gap-2">
              <p className="font-bold ">{`Verified: ${DateTime.now().toLocaleString()}`}</p>

              <img src="/assets/images/green_status.png" className="w-4 h-4" alt="Verified" />
            </div>

            {qrCode.length > 0 ? (
              <div
                data-testid="qr-code"
                className="flex flex-col items-center gap-2 py-6 bg-white rounded-box shadow-md"
              >
                <QRCode value={qrCode} />
                <div className="flex  gap-2 justify-center items-center">
                  <p className="text-lg  text-neutral font-semibold">SMART® Health Card</p>
                  <img
                    src="/assets/images/SMART.png"
                    className="w-[35px] h-[25px] ml-auto"
                    alt="SMART"
                  />
                </div>
              </div>
            ) : loadingVCI ? (
              <Squirrel />
            ) : (
              <span className="text-center">Unable to fetch QR code. Please try again later.</span>
            )}
            {isIOSMobile && qrCode.length > 0 && (
              <>
                <img
                  src="/assets/images/apple_health.png"
                  className="w-full cursor-pointer"
                  alt="Add to Apple Health"
                  onClick={() =>
                    window.open(
                      `https://redirect.health.apple.com/SMARTHealthCard/${qrCode.slice(
                        5,
                        qrCode.length
                      )}`,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                />
              </>
            )}
            <div className="flex flex-col">
              <p className="text-xs">
                SMART® Health Cards may contain only a limited set of COVID-19 vaccine doses. For
                more information about SMART® Health Cards:
              </p>
              <a
                className="text-xs underline text-blue-400"
                href="https://smarthealth.cards/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://smarthealth.cards/
              </a>
            </div>
          </div>
        </div>
      );
    };

    return (
      <ModalV2
        modalBoxClassName="bg-base-100 max-w-xl"
        ref={ref}
        onClose={() => {
          setAttemptMade(false);
          onClose?.();
        }}
      >
        <div>
          <ModalV2Header className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <ModalV2BackButton />
              <p className="font-semibold text-xl">SMART® Health Card</p>
            </div>
          </ModalV2Header>
        </div>
        <ModalV2Body className="bg-base-100 pt-2">{renderContent()}</ModalV2Body>
      </ModalV2>
    );
  }
);

export default SmartHealthCardModal;
